/**
 * Video Layer Renderer
 * Renders video layers with object-fit support
 */

import React, { useRef, useEffect } from 'react';
import type { VideoLayer } from '@/types/layers';
import type { ResolvedPosition } from '@/lib/position/resolver';
import { useEditorStore } from '@/stores/editorStore';

interface VideoLayerRendererProps {
  layer: VideoLayer;
  position: ResolvedPosition;
  isSelected?: boolean;
  isHovered?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export function VideoLayerRenderer({
  layer,
  position,
  isSelected,
  isHovered,
  onClick,
  onMouseDown,
  onMouseEnter,
  onMouseLeave,
}: VideoLayerRendererProps) {
  const { content, transform, opacity, width, height } = layer;
  const videoRef = useRef<HTMLVideoElement>(null);
  const isPlaying = useEditorStore((s) => s.isPlaying);

  const resolvedWidth = position.width ?? (typeof width === 'number' ? width : 'auto');
  const resolvedHeight = position.height ?? (typeof height === 'number' ? height : 'auto');

  // Sync video playback with editor timeline
  useEffect(() => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.play().catch(() => {
        // Autoplay might be blocked, which is fine
      });
    } else {
      videoRef.current.pause();
    }
  }, [isPlaying]);

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    left: position.x,
    top: position.y,
    width: resolvedWidth,
    height: resolvedHeight,
    opacity,
    transform: transform
      ? `rotate(${transform.rotation ?? 0}deg) scale(${transform.scaleX ?? 1}, ${transform.scaleY ?? 1})`
      : undefined,
    transformOrigin: transform
      ? `${(transform.originX ?? 0.5) * 100}% ${(transform.originY ?? 0.5) * 100}%`
      : undefined,
    overflow: 'hidden',
    borderRadius: content.borderRadius ?? 0,
    // Hover styling (selection handled by SelectionOverlay)
    outline: isHovered && !isSelected ? '1px dashed #3B82F6' : 'none',
    outlineOffset: 2,
    cursor: 'pointer',
    pointerEvents: layer.locked ? 'none' : 'auto',
  };

  const videoStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: content.objectFit ?? 'cover',
    objectPosition: content.objectPosition ?? 'center',
    display: 'block',
  };

  return (
    <div
      style={containerStyle}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-layer-id={layer.id}
    >
      <video
        ref={videoRef}
        src={content.src}
        poster={content.poster}
        style={videoStyle}
        loop={content.loop ?? true}
        muted={content.muted ?? true}
        autoPlay={content.autoplay ?? true}
        playsInline
      />
    </div>
  );
}
